import axios from "axios";

const STORAGE_PREFIX = "pyramid-sync:";

export const buildSyncPendingMessage = (entityLabel) =>
  `${entityLabel} guardado correctamente. La sincronizacion de permisos esta en proceso. Te avisaremos aqui cuando ya puedas revisar el cambio.`;

export const buildSyncCompletedMessage = (entityLabel) =>
  `${entityLabel} guardado correctamente. La sincronizacion de permisos finalizo y ya puedes revisar el cambio reflejado en permisos.`;

export const buildSyncFailedMessage = (entityLabel, syncMessage) =>
  `${entityLabel} guardado correctamente, pero la sincronizacion de permisos no finalizo correctamente.${syncMessage ? ` ${syncMessage}` : ""}`;

export const startPyramidSyncMonitor = ({
  baseUrl,
  executionId,
  onCompleted,
  onFailed,
  intervalMs = 5000,
  maxAttempts = 120,
}) => {
  if (!executionId) {
    return () => {};
  }

  let cancelled = false;
  let attempts = 0;
  let consecutiveErrors = 0;
  let timerId = null;

  const scheduleNextPoll = (delay = intervalMs) => {
    if (!cancelled) {
      timerId = setTimeout(poll, delay);
    }
  };

  const poll = async () => {
    if (cancelled) {
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/sync-status/${executionId}`, {
        timeout: 15000,
        params: {
          _ts: Date.now(),
        },
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      const sync = response?.data?.sync ?? response?.data ?? null;
      consecutiveErrors = 0;

      if (sync?.status === "pending") {
        attempts += 1;

        if (attempts >= maxAttempts) {
          attempts = 0;
        }

        scheduleNextPoll();
        return;
      }

      if (sync?.status === "completed") {
        onCompleted?.(sync);
        return;
      }

      if (sync?.status === "failed") {
        onFailed?.(sync);
        return;
      }

      consecutiveErrors += 1;
      scheduleNextPoll(Math.min(intervalMs * consecutiveErrors, 30000));
    } catch (error) {
      consecutiveErrors += 1;
      scheduleNextPoll(Math.min(intervalMs * consecutiveErrors, 30000));
    }
  };

  scheduleNextPoll();

  return () => {
    cancelled = true;

    if (timerId) {
      clearTimeout(timerId);
    }
  };
};

export const savePyramidSyncSession = (storageKey, sync, extra = {}) => {
  if (!storageKey || !sync?.executionId) {
    return;
  }

  sessionStorage.setItem(
    `${STORAGE_PREFIX}${storageKey}`,
    JSON.stringify({
      executionId: sync.executionId,
      status: sync.status ?? "pending",
      savedAt: new Date().toISOString(),
      ...extra,
    }),
  );
};

export const loadPyramidSyncSession = (storageKey) => {
  if (!storageKey) {
    return null;
  }

  try {
    const rawValue = sessionStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch {
    return null;
  }
};

export const clearPyramidSyncSession = (storageKey) => {
  if (!storageKey) {
    return;
  }

  sessionStorage.removeItem(`${STORAGE_PREFIX}${storageKey}`);
};
